import React, { useState, useEffect, useContext } from "react";

import {
    Hospital,
    HeartPulse,
    ArrowRight,
    Sparkles,
    ShieldCheck,
    BarChart3,
    XCircle,
    CheckCircle,
    Download,
    Activity,
    Bed,
    ClipboardCheck,
    PlayCircle,
    Stethoscope,
    FileText,
    Users,
    Bot,
    Database,
    Settings,
} from "lucide-react";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";

import Head from "../../Components/Layout/Head";
import FooterNew from "../../Components/Layout/FooterNew";
import { CountryContext } from "../../CountryContext";
import ApiPath from "../../Components/Common/Apiurl";

import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";
import getHelmetContent from "../../Components/Common/HelmetData";

const hospitalFeatures = [
    {
        id: 1,
        name: "AI Hospital Dashboard & Predictive Analytics",
        purpose: "Executive Leadership / Governance",
        currentEffort: "Manual MIS review, cross-department follow-ups and Excel consolidation.",
        afterSystem: "Auto dashboards with alerts, forecasting and department-wise visibility.",
        benefit: "Faster strategic decisions on patient load, occupancy, revenue, compliance and department performance.",
        details: "Integrates with HIS/EMR, billing, admission and department data to generate executive dashboards and predictive insights.",
    },
    {
        id: 2,
        name: "Clinical Performance Analytics & Infection Risk Prediction",
        purpose: "Medical Services Division",
        currentEffort: "Manual audit of clinical indicators, infection trends and workload gaps.",
        afterSystem: "AI highlights clinical risk patterns, department exceptions and infection-risk indicators.",
        benefit: "Improved quality monitoring, better infection control visibility and stronger clinical governance.",
        details: "Uses historical patient data, infection records, ward data and clinical parameters to identify early warning patterns.",
    },
    {
        id: 3,
        name: "Smart Bed Allocation, Discharge Prediction & OPD Queue Optimization",
        purpose: "Patient Care Operations - OPD / IPD / ICU / OT",
        currentEffort: "Manual bed coordination, discharge follow-ups and queue pressure handling.",
        afterSystem: "Priority-based bed recommendations, discharge readiness alerts and queue optimization.",
        benefit: "Reduced waiting time, better bed utilization, smoother patient movement and improved patient experience.",
        details: "Connects admission, discharge, OPD token, ward and ICU data to recommend operational actions.",
    },
    {
        id: 4,
        name: "ICU Early Warning & Critical Care Response Support",
        purpose: "ICU / Critical Care",
        currentEffort: "Manual vitals review and delayed escalation during high-load situations.",
        afterSystem: "Risk alerts based on vitals, lab trends, clinical parameters and deterioration signals.",
        benefit: "Faster escalation support and improved critical-care responsiveness.",
        details: "Uses vitals, reports and ICU monitoring data to identify patients requiring urgent attention.",
    },
    {
        id: 5,
        name: "Patient History Summarization & AI-Assisted Diagnosis Support",
        purpose: "Specialty Clinical Departments",
        currentEffort: "Doctors manually scan long histories, previous reports, prescriptions and discharge summaries.",
        afterSystem: "Concise clinical summaries and decision-support prompts for doctor review.",
        benefit: "Reduced doctor workload, faster case understanding and better consultation preparation.",
        details: "Creates structured summaries from EMR, PDFs, lab reports, prescriptions and previous visit notes.",
    },
    {
        id: 6,
        name: "Lab / Radiology Report Summarization & Trend Analysis",
        purpose: "Diagnostics & Ancillary Services",
        currentEffort: "Manual reading of long reports and comparison with older values.",
        afterSystem: "AI summaries, abnormality highlights and previous-vs-current trend comparison.",
        benefit: "Faster report interpretation and early visibility of abnormal trends.",
        details: "Reads lab/radiology reports and highlights changes, risk markers and important observations.",
    },
    {
        id: 7,
        name: "Billing Validation, Claim Document Processing & Revenue Forecasting",
        purpose: "Revenue Cycle / Finance",
        currentEffort: "Manual bill checks, claim document review and collection tracking.",
        afterSystem: "AI-assisted bill validation, claim checklisting and revenue visibility dashboards.",
        benefit: "Reduced billing errors, faster claim processing and stronger revenue-cycle discipline.",
        details: "Checks bills, insurance documents, discharge summaries and claim requirements for missing or inconsistent information.",
    },
    {
        id: 8,
        name: "AI Resume Screening, Attrition Prediction & HR Analytics",
        purpose: "Human Resources",
        currentEffort: "Manual CV screening, HR reporting and attrition review.",
        afterSystem: "Automated shortlist assistance, HR dashboards and attrition-risk insights.",
        benefit: "Faster hiring, better workforce planning and data-driven HR decisions.",
        details: "Analyzes resumes, staff data, attendance, department load and HR trends.",
    },
    {
        id: 9,
        name: "Medicine Demand Forecasting, Inventory Alerts & Vendor Analytics",
        purpose: "Supply Chain / Pharmacy / Materials",
        currentEffort: "Manual stock review, purchase planning, expiry tracking and vendor follow-ups.",
        afterSystem: "Predictive stock alerts, demand planning and vendor scorecards.",
        benefit: "Lower stockouts, reduced wastage and optimized procurement planning.",
        details: "Uses consumption patterns, seasonal demand, department usage and vendor performance data.",
    },
    {
        id: 10,
        name: "Internal IT Chatbot, Log Anomaly Detection & Usage Analytics",
        purpose: "Information Technology",
        currentEffort: "Repeated IT support queries and manual log monitoring.",
        afterSystem: "Self-service IT support chatbot, anomaly alerts and usage analytics.",
        benefit: "Reduced IT workload and improved system security, uptime and performance monitoring.",
        details: "Supports staff queries, monitors logs, detects unusual access and routes IT issues faster.",
    },
    {
        id: 11,
        name: "Predictive Maintenance, Energy Analytics & Safety Alerts",
        purpose: "Facilities & Engineering",
        currentEffort: "Manual equipment checks, HVAC review, reactive maintenance and energy monitoring.",
        afterSystem: "Maintenance alerts, equipment risk signals and energy consumption analytics.",
        benefit: "Lower downtime, better safety compliance and reduced operating waste.",
        details: "Tracks equipment health, maintenance schedules, energy patterns and facility alerts.",
    },
    {
        id: 12,
        name: "Patient Chatbot, Smart Scheduling & Feedback Sentiment Analysis",
        purpose: "Patient Experience",
        currentEffort: "Manual appointment calls, repetitive FAQs and feedback review.",
        afterSystem: "24/7 chatbot, scheduling support and patient feedback sentiment dashboard.",
        benefit: "Better patient satisfaction, fewer repetitive calls and reduced waiting friction.",
        details: "Handles FAQs, appointment guidance, follow-ups, reminders and feedback classification.",
    },
    {
        id: 13,
        name: "AI CCTV Analytics, Ambulance Routing & Parking Management",
        purpose: "Security & Transport",
        currentEffort: "Manual CCTV monitoring, ambulance route coordination and parking management.",
        afterSystem: "Crowd alerts, intrusion detection, route suggestions and parking flow insights.",
        benefit: "Improved safety, faster emergency response and smoother campus movement.",
        details: "Uses CCTV feeds, security data, emergency movement and parking patterns for alerts.",
    },
    {
        id: 14,
        name: "Patient Segmentation, Campaign Analytics & Referral Insights",
        purpose: "Marketing & Outreach",
        currentEffort: "Manual campaign analysis and generic outreach.",
        afterSystem: "Segmented outreach, referral analytics and campaign performance tracking.",
        benefit: "Better marketing targeting, improved referral visibility and stronger patient acquisition planning.",
        details: "Analyzes patient segments, referral sources, campaign response and department-wise demand.",
    },
    {
        id: 15,
        name: "Research Data Extraction, Clinical Trial Matching & CSR Eligibility Analytics",
        purpose: "Academics, Research & Charity",
        currentEffort: "Manual data extraction, screening and eligibility evaluation.",
        afterSystem: "AI-assisted extraction, matching, scoring and transparency dashboards.",
        benefit: "Faster research execution, fair fund allocation and better CSR transparency.",
        details: "Extracts structured data from records and supports eligibility matching for research, trials and charity programs.",
    },
];

const valueAreas = [
    {
        icon: <Bed size={24} />,
        title: "Patient Flow",
        text: "Queue optimization, bed allocation, discharge readiness, OPD scheduling and appointment support.",
    },
    {
        icon: <HeartPulse size={24} />,
        title: "Clinical Support",
        text: "Patient history summaries, diagnostics trends, ICU warnings, infection-risk analytics and doctor-assistive summaries.",
    },
    {
        icon: <ClipboardCheck size={24} />,
        title: "Revenue & Operations",
        text: "Billing validation, claim processing, inventory alerts, HR analytics, IT support and workflow automation.",
    },
    {
        icon: <Hospital size={24} />,
        title: "Management Visibility",
        text: "Hospital dashboards, predictive analytics, facilities alerts, marketing insights and CSR reporting.",
    },
];

const implementationAreas = [
    {
        icon: <Database size={22} />,
        title: "Connect Existing Systems",
        text: "AI can work on top of HIS, EMR, LIS, RIS/PACS, billing, HRMS, ERP and document repositories without replacing current hospital software.",
    },
    {
        icon: <Bot size={22} />,
        title: "Create AI Assistants",
        text: "Role-based assistants can support doctors, nurses, billing teams, front desk, pharmacy, HR, IT and management teams.",
    },
    {
        icon: <ShieldCheck size={22} />,
        title: "Secure Access Control",
        text: "Department-wise access, audit logs, user roles and private deployment options help protect sensitive hospital data.",
    },
    {
        icon: <Settings size={22} />,
        title: "Phased Implementation",
        text: "Hospitals can start with dashboards, chatbot, summaries and billing checks, then expand to predictive analytics and advanced automation.",
    },
];

const HospitalAI = () => {
    const [selectedCountry, setSelectedCountry] = useState("India");

    const country = useContext(CountryContext);
    const websiteURL = window.location.origin;

    const [formVisible, setFormVisible] = useState(true);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [phone, setPhone] = useState("");

    useEffect(() => {
        AOS.init({
            duration: 900,
            easing: "ease-out-cubic",
            once: true,
            offset: 80,
        });
    }, []);

    const countryOptions = [
        "Australia",
        "Germany",
        "India",
        "New Zealand",
        "United States",
        "Other",
    ];

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        country: "",
        message: "",
        firmType: "Hospital",
    });

    const [errors, setErrors] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        firmType: "",
        message: "",
    });

    useEffect(() => {
        if (countryOptions.includes(country)) {
            setSelectedCountry(country);
        } else {
            setSelectedCountry("India");
        }
    }, [country]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value,
        });

        setErrors({
            ...errors,
            [name]: "",
        });

        if (name === "country") {
            setSelectedCountry(value);
            setPhone("");

            setErrors((prev) => ({
                ...prev,
                phone: "",
                country: "",
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = {
            name: "",
            email: "",
            phone: "",
            country: "",
            firmType: "",
            message: "",
        };

        let isValid = true;

        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
            isValid = false;
        }

        const emailRegex = /^\S+@\S+\.\S+$/;

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
            isValid = false;
        } else if (!emailRegex.test(formData.email.trim())) {
            newErrors.email = "Invalid email format";
            isValid = false;
        }

        if (!phone) {
            newErrors.phone = "Phone number is required";
            isValid = false;
        } else if (!isValidPhoneNumber(phone)) {
            newErrors.phone = "Please enter a valid phone number";
            isValid = false;
        }

        if (!selectedCountry) {
            newErrors.country = "Please select country";
            isValid = false;
        }

        if (formData.message.trim().length > 300) {
            newErrors.message = "Maximum character limit is 300";
            isValid = false;
        }

        setErrors(newErrors);

        if (!isValid) return;

        const payload = {
            name: formData.name,
            email: formData.email.trim(),
            phone: phone,
            lookingFor: "Healthcare",
            queryPageIndex: "HOSPITAL_AI_PAGE",
            message:
                (formData.message || "") +
                ` IP: ${country} dropdown: ${selectedCountry} | URL: ${websiteURL}`,
        };

        try {
            setLoading(true);
            setFormError("");

            const response = await fetch(ApiPath + "/tqenquiry", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setFormData({
                    name: "",
                    email: "",
                    firmType: "Hospital",
                    message: "",
                });

                setPhone("");
                setFormVisible(false);

                setTimeout(() => {
                    setFormVisible(true);
                    setLoading(false);
                }, 5000);
            } else {
                setFormError("Something went wrong. Please try again.");
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
            setFormError("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    const getCountryCode = (countryName) => {
        const map = {
            India: "IN",
            "United States": "US",
            Australia: "AU",
            Germany: "DE",
            "New Zealand": "NZ",
        };

        return map[countryName] || "IN";
    };

    const StatCard = ({ icon, label, value, subText }) => (
        <div className="rounded-[2rem] border border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_20px_70px_rgba(15,23,42,0.08)] p-6 lg:p-7 hover:-translate-y-1 transition-all duration-300">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                {icon}
            </div>

            <p className="mt-5 text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">
                {label}
            </p>

            <h3 className="mt-3 text-2xl xl:text-2xl font-black text-slate-900 leading-tight break-words">
                {value}
            </h3>

            <p className="mt-3 text-slate-500 leading-7 text-sm">
                {subText}
            </p>
        </div>
    );

    const pageTitle = " AI Automation For Hospital Operations - ThinkQuotient AI";
    const metaTitle = " AI Automation For Hospital Operations - ThinkQuotient AI";
    const helmetContent = getHelmetContent();

    return (
        <>

            <Helmet>
                <title>{pageTitle}</title>
                <meta name="title" content={metaTitle} />
                {/* Remaining meta tags */}
                {helmetContent.meta.map((metaTag, index) => (
                    <meta key={index} name={metaTag.name} content={metaTag.content} />
                ))}

                {/* Script for Facebook */}
                {helmetContent.script}

                {/* Updated Google Tag Manager */}
                <script>
                    {`
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','GTM-KTNBVH9V');
                  `}
                </script>

                {/* Google Analytics Scripts */}
                <script async src="https://www.googletagmanager.com/gtag/js?id=UA-143016865-1"></script>
                <script>
                    {`
                  window.dataLayer = window.dataLayer || []; 
                  function gtag() { 
                    dataLayer.push(arguments); 
                  } 
                  gtag("js", new Date()); 
                  gtag("config", "UA-143016865-1");
                  `}
                </script>

                <script async src="https://www.googletagmanager.com/gtag/js?id=G-BSFPPN5E9D"></script>
                <script>
                    {`
                  window.dataLayer = window.dataLayer || [];
                  function gtag() { 
                    dataLayer.push(arguments); 
                  }
                  gtag('js', new Date());
                  gtag('config', 'G-BSFPPN5E9D');
                  `}
                </script>

                <script async src="https://www.googletagmanager.com/gtag/js?id=AW-574047969"></script>
                <script>
                    {`
                  window.dataLayer = window.dataLayer || [];
                  function gtag() { 
                    dataLayer.push(arguments); 
                  }
                  gtag('js', new Date());
                  gtag('config', 'AW-574047969');
                  `}
                </script>

                {/* Google Tag Manager noscript fallback */}
                <noscript>{`<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-KTNBVH9V" height="0" width="0" style="display:none;visibility:hidden"></iframe>`}</noscript>

                {/* Facebook Pixel Code */}
                <script>{`
                  !function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  fbq('init', '473030589157148');
                  fbq('track', 'PageView');
                `}</script>
                <noscript>{`
                  <img height="1" width="1" style="display:none"
                  src="https://www.facebook.com/tr?id=473030589157148&ev=PageView&noscript=1"/>
                `}</noscript>

            </Helmet>

            <Head />

            <div className="min-h-screen overflow-hidden bg-slate-50 text-slate-900 relative">
                {/* HERO */}
                <section className="relative py-20 lg:py-14 overflow-hidden">
                    <div className="absolute top-[-10rem] left-[-10rem] w-[30rem] h-[30rem] bg-blue-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-[-10rem] right-[-10rem] w-[30rem] h-[30rem] bg-indigo-500/10 rounded-full blur-3xl"></div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-20 items-center">
                            <div className="lg:col-span-7 xl:col-span-7" data-aos="fade-right">
                                <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                    <Sparkles size={16} />
                                    AI Solutions For Hospitals
                                </div>

                                <h1 className="mt-8 text-4xl sm:text-5xl xl:text-5xl font-black leading-[0.95] tracking-[-0.05em] text-slate-900">
                                    AI Automation
                                    <span className="block mt-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                        For Hospital Operations
                                    </span>
                                </h1>

                                <p className="mt-4 text-lg leading-9 text-slate-600 max-w-2xl">
                                    Improve hospital operations with AI-enabled dashboards, clinical summaries,
                                    patient communication automation, billing checks, inventory forecasting,
                                    workflow assistance and management visibility.
                                </p>

                                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                    <a
                                        href="#contact-form"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold shadow-xl hover:-translate-y-1 transition-all duration-300"
                                    >
                                        Get AI Consultation
                                        <ArrowRight size={18} />
                                    </a>

                                    <a
                                        href="#demo-video"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 border border-slate-200 bg-white/80 backdrop-blur-xl text-slate-800 font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                                    >
                                        Watch Demo
                                        <PlayCircle size={18} />
                                    </a>

                                    <a
                                        href="/assets/pdfs/AI-Enabled Hospital Operations Automation.pdf"
                                        download
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 border border-slate-200 bg-white/80 backdrop-blur-xl text-slate-800 font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                                    >
                                        Download PDF
                                        <Download size={18} />
                                    </a>
                                </div>
                            </div>

                            <div className="relative lg:col-span-5 xl:col-span-5" data-aos="fade-left">
                                <div className="relative rounded-[2.5rem] border border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_30px_90px_rgba(15,23,42,0.12)] p-4">
                                    <img
                                        src="/assets/images/front-view-nurses-team-hospital.jpg"
                                        alt="Hospital AI Automation"
                                        className="w-full rounded-[2rem] object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* BENEFITS SECTION */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-12 gap-10 items-start">
                            <div className="lg:col-span-8" data-aos="fade-up">
                                <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                    <BarChart3 size={16} />
                                    AI Feature Benefits
                                </div>

                                <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                    Practical AI Benefits For Hospital Teams
                                </h2>

                                <p className="mt-6 text-lg leading-9 text-slate-600 max-w-3xl">
                                    This section explains how each AI feature supports real hospital workflows.
                                    The focus is on operational improvement, patient experience, decision support,
                                    automation and management visibility.
                                </p>

                                <div className="mt-8 flex flex-wrap gap-3">
                                    {[
                                        "Better patient flow",
                                        "Faster clinical summaries",
                                        "Improved billing validation",
                                        "Smarter inventory planning",
                                        "Stronger management visibility",
                                    ].map((chip, index) => (
                                        <div
                                            key={index}
                                            className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm"
                                        >
                                            {chip}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="lg:col-span-4" data-aos="zoom-in" data-aos-delay="100">
                                <div className="sticky top-28 rounded-[2rem] border border-white/40 bg-white/90 backdrop-blur-xl shadow-[0_20px_70px_rgba(15,23,42,0.08)] p-6 lg:p-8">
                                    <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                        <Hospital size={30} />
                                    </div>

                                    <h3 className="mt-6 text-2xl font-black text-slate-900">
                                        Not an HMS Replacement
                                    </h3>

                                    <p className="mt-4 text-slate-600 leading-8">
                                        The AI layer works on top of existing hospital systems such as HIS, EMR,
                                        LIS, RIS/PACS, billing, HRMS, ERP and document repositories.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* STATS */}
                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            <div data-aos="fade-up" data-aos-delay="0">
                                <StatCard
                                    icon={<Stethoscope size={24} />}
                                    label="Clinical Assistance"
                                    value="Summaries & Risk Alerts"
                                    subText="Supports doctors, ICU teams, diagnostics and quality departments with faster review."
                                />
                            </div>

                            <div data-aos="fade-up" data-aos-delay="100">
                                <StatCard
                                    icon={<Users size={24} />}
                                    label="Operational Assistance"
                                    value="Workflow Automation"
                                    subText="Helps OPD, IPD, pharmacy, billing, HR, IT and admin teams reduce repetitive work."
                                />
                            </div>

                            <div data-aos="fade-up" data-aos-delay="200">
                                <StatCard
                                    icon={<ShieldCheck size={24} />}
                                    label="Secure AI Adoption"
                                    value="Private & Controlled"
                                    subText="Can be implemented with role-based access, audit logs and secure deployment options."
                                />
                            </div>
                        </div>

                        {/* TABLE */}
                        <div
                            className="mt-14 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_70px_rgba(15,23,42,0.06)]"
                            data-aos="fade-up"
                        >
                            <div className="px-6 lg:px-8 py-8 border-b border-slate-100">
                                <h3 className="text-2xl font-black text-slate-900">
                                    Feature-Wise AI Benefits Matrix
                                </h3>
                                <p className="mt-3 text-slate-600 leading-7">
                                    A clear business view of what each AI feature does and how it improves
                                    hospital operations.
                                </p>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-[1400px] w-full">
                                    <thead className="bg-gradient-to-r from-blue-600 to-indigo-600">
                                        <tr>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                #
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                AI Feature
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Department / Users
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Current Challenge
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                What AI Does
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Hospital Benefit
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Implementation Detail
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {hospitalFeatures.map((item) => (
                                            <tr key={item.id} className="hover:bg-slate-50 transition-all duration-200">
                                                <td className="px-6 py-5 border-b border-slate-100 align-top">
                                                    <span className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 font-black flex items-center justify-center">
                                                        {item.id}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top">
                                                    <strong className="text-slate-900 leading-7 block">
                                                        {item.name}
                                                    </strong>
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-600 font-medium leading-7">
                                                    {item.purpose}
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-500 leading-7">
                                                    {item.currentEffort}
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-500 leading-7">
                                                    {item.afterSystem}
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top">
                                                    <span className="inline-flex rounded-2xl bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 leading-6">
                                                        {item.benefit}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-500 leading-7">
                                                    {item.details}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <p className="mt-3 text-xs text-slate-500 leading-6">
                            *The above feature matrix is for business discussion and solution planning.
                            Final implementation scope depends on hospital systems, available APIs, data quality,
                            user roles, security policy and department-wise workflow requirements.
                        </p>
                    </div>
                </section>

                {/* HOSPITAL VALUE AREAS */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                <Activity size={16} />
                                AI Opportunity Areas
                            </div>

                            <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                Where AI Creates Hospital Value
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600">
                                Start with practical, low-disruption workflows and then expand into advanced
                                predictive intelligence across departments.
                            </p>
                        </div>

                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                            {valueAreas.map((item, index) => (
                                <div
                                    key={index}
                                    className="rounded-[2rem] border border-slate-200 bg-slate-50 p-7 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center">
                                        {item.icon}
                                    </div>
                                    <h3 className="mt-6 text-xl font-black text-slate-900">
                                        {item.title}
                                    </h3>
                                    <p className="mt-4 text-slate-600 leading-7">
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* IMPLEMENTATION APPROACH */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                <FileText size={16} />
                                Implementation Approach
                            </div>

                            <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                AI Layer On Top Of Existing Hospital Software
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600">
                                The solution can be deployed in phases without disturbing existing hospital operations.
                            </p>
                        </div>

                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                            {implementationAreas.map((item, index) => (
                                <div
                                    key={index}
                                    className="rounded-[2rem] border border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_20px_70px_rgba(15,23,42,0.08)] p-7 hover:-translate-y-1 transition-all duration-300"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                        {item.icon}
                                    </div>
                                    <h3 className="mt-6 text-xl font-black text-slate-900">
                                        {item.title}
                                    </h3>
                                    <p className="mt-4 text-slate-600 leading-7">
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* VIDEO */}
                <section id="demo-video" className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-0 md:px-4 sm:px-6 lg:px-8">
                        <div className="text-center px-4" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 px-5 py-3 md:rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                <PlayCircle size={16} />
                                AI Hospital Workflow Demo
                            </div>

                            <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                See How AI Can Transform Hospital Operations
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600 max-w-3xl mx-auto">
                                Practical automation workflows for hospitals, clinics and healthcare operations teams.
                            </p>
                        </div>

                        <div
                            className="mt-12 md:rounded-[2.5rem] bg-white border border-slate-200 shadow-[0_20px_70px_rgba(15,23,42,0.06)] p-5 lg:p-7"
                            data-aos="zoom-in-up"
                            data-aos-delay="150"
                        >
                            <div className="overflow-hidden md:rounded-[2rem] bg-black">
                                <video className="w-full aspect-video object-contain" controls autoPlay muted loop playsInline>
                                    <source src="/assets/videos/Hospital-AI.mp4" type="video/mp4" />
                                </video>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CONTACT FORM SECTION */}
                <section id="contact-form" className="relative py-20 sm:py-24 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>

                    <div className="relative max-w-7xl mx-auto px-0 md:px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-10 items-center">
                            <div className="px-6 lg:col-span-1 xl:col-span-3" data-aos="fade-right">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm border border-blue-200">
                                    <Sparkles size={16} />
                                    AI Consultation For Hospitals
                                </div>

                                <h2 className="mt-6 text-4xl sm:text-5xl font-black leading-tight text-slate-900">
                                    Build Secure
                                    <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                        AI For Hospital Operations
                                    </span>
                                </h2>

                                <p className="mt-6 text-lg text-slate-600 leading-8 max-w-xl">
                                    Automate patient communication, improve OPD/IPD workflows, support doctors with summaries,
                                    reduce billing errors, improve inventory planning, and create department-wise hospital dashboards.
                                </p>

                                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {[
                                        "Improve patient flow",
                                        "Reduce manual reporting",
                                        "Automate patient communication",
                                        "Create secure AI dashboards",
                                    ].map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-4 shadow-sm"
                                        >
                                            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
                                                <CheckCircle size={18} />
                                            </div>

                                            <span className="font-medium text-slate-700">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="relative lg:col-span-1 xl:col-span-2" data-aos="fade-left" data-aos-delay="100">
                                <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-[2rem] blur-xl opacity-20"></div>

                                <div className="relative bg-white/90 backdrop-blur-xl border border-white/40 md:rounded-[2rem] shadow-[0_25px_80px_rgba(0,0,0,0.08)] overflow-hidden">
                                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center backdrop-blur-md">
                                                <Hospital size={28} />
                                            </div>

                                            <div>
                                                <h3 className="text-2xl font-bold">Book AI Consultation</h3>
                                                <p className="text-blue-100 mt-1 text-sm">
                                                    Tell us about your hospital automation requirements
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 sm:p-8">
                                        {!formVisible && (
                                            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-5 py-6 rounded-2xl mb-6">
                                                <div className="flex flex-col items-center text-center">
                                                    <CheckCircle size={52} className="text-blue-600 mb-4" />
                                                    <h3 className="text-xl font-bold mb-2">Thank You!</h3>
                                                    <p className="text-sm sm:text-base">Our team will contact you shortly.</p>
                                                </div>
                                            </div>
                                        )}

                                        {formError && (
                                            <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-6 rounded-2xl mb-6">
                                                <div className="flex flex-col items-center text-center">
                                                    <XCircle size={48} className="text-red-500 mb-3" />
                                                    <h3 className="text-lg font-bold mb-2">Submission Failed</h3>
                                                    <p className="text-sm">{formError}</p>
                                                </div>
                                            </div>
                                        )}

                                        {formVisible && (
                                            <form onSubmit={handleSubmit} className="space-y-5">
                                                <div>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        placeholder="Full Name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.name
                                                            ? "border-red-500"
                                                            : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.name && <p className="text-red-500 text-sm mt-2">{errors.name}</p>}
                                                </div>

                                                <div>
                                                    <div
                                                        className={`rounded-2xl border bg-slate-50 px-4 py-4 transition-all duration-300 ${errors.phone
                                                            ? "border-red-500"
                                                            : "border-slate-200 focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    >
                                                        <PhoneInput
                                                            key={selectedCountry}
                                                            countries={["IN", "US", "AU", "DE", "JP", "SG", "NZ"]}
                                                            defaultCountry={getCountryCode(selectedCountry)}
                                                            international
                                                            value={phone}
                                                            onChange={(value) => {
                                                                setPhone(value || "");
                                                                setErrors({ ...errors, phone: "" });
                                                            }}
                                                            placeholder="Enter phone number"
                                                        />
                                                    </div>
                                                    {errors.phone && <p className="text-red-500 text-sm mt-2">{errors.phone}</p>}
                                                </div>

                                                <div>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        placeholder="Email Address"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.email
                                                            ? "border-red-500"
                                                            : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.email && <p className="text-red-500 text-sm mt-2">{errors.email}</p>}
                                                </div>

                                                <div>
                                                    <select
                                                        id="country"
                                                        name="country"
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.country
                                                            ? "border-red-500"
                                                            : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                        onChange={handleChange}
                                                        value={selectedCountry}
                                                    >
                                                        {countryOptions.map((countryName) => (
                                                            <option key={countryName} value={countryName}>
                                                                {countryName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.country && <p className="text-red-500 text-sm mt-2">{errors.country}</p>}
                                                </div>

                                                <div>
                                                    <textarea
                                                        rows={3}
                                                        name="message"
                                                        placeholder="Tell us which hospital workflows, departments, patient communication processes, billing activities, or dashboards you want to automate"
                                                        value={formData.message}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl bg-slate-50 px-5 py-4 outline-none transition-all duration-300 resize-none ${errors.message
                                                            ? "border border-red-500"
                                                            : "border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.message && <p className="text-red-500 text-sm mt-2">{errors.message}</p>}
                                                </div>

                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className={`w-full inline-flex items-center justify-center gap-2 rounded-2xl py-4 text-lg font-semibold text-white transition-all duration-300 ${loading
                                                        ? "bg-slate-400 cursor-not-allowed"
                                                        : "bg-gradient-to-r from-blue-600 to-indigo-600 shadow-xl hover:scale-[1.02]"
                                                        }`}
                                                >
                                                    {loading ? "Submitting..." : "Request AI Consultation"}
                                                    {!loading && <ArrowRight size={18} />}
                                                </button>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <FooterNew />
            </div>
        </>
    );
};

export default HospitalAI;